import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import {
  addLandMapLayers,
  basemapStyleUrl,
  landMapBounds,
  legendItemsFromConfig,
  LAND_MAP_FIT_PADDING,
  LAND_MAP_FIT_MAX_ZOOM
} from './maps/landMapLayers';
import AtlasMapRuntime from './maps/atlasMapRuntime';
import AtlasLandRuntime from './maps/atlasLandRuntime';
import AtlasMapLegend from './maps/AtlasMapLegend';
import useAtlasTheme from './useAtlasTheme';

// Touch screens: one finger scrolls the chat, two fingers move the map (a map
// is most of a phone's thread, so one-finger panning trapped the scroll).
const COOPERATIVE_GESTURES = typeof window !== 'undefined'
  && Boolean(window.matchMedia?.('(pointer: coarse)').matches);

const DEFAULT_MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  process.env.REACT_APP_MAPBOX_GL_TOKEN ||
  'pk.eyJ1IjoibWljaGFlbC1ydCIsImEiOiJjbTFmY2gxZGYydjBhMmptejRkMXY3NnE4In0.HLgD2rXqws3hQ0Wr7X7PQw';

const MAPBOX_TILESETS = {
  state: {
    id: 'michael-rt.9fowxiad',
    source_layer: 'us_states-9ghuuu',
    geoid_property: 'GEOID',
    name_property: 'NAME'
  },
  county: {
    id: 'michael-rt.3at1878p',
    source_layer: 'us_counties-4kvhkl',
    geoid_property: 'GEOID',
    name_property: 'NAME'
  },
  zcta: {
    id: 'michael-rt.4c3ijtve',
    source_layer: 'us_zctas-8qweax',
    geoid_property: 'GEOID',
    name_property: null
  },
  tract: {
    id: 'michael-rt.9gixg01e',
    source_layer: 'us_tracts-6zymb3',
    geoid_property: 'GEOID',
    name_property: 'NAME'
  },
  h3_hex: {
    id: 'michael-rt.4uco037u',
    source_layer: 'h3_hex_r8_tileset-3cxn9m',
    geoid_property: 'hex_id',
    name_property: null
  }
};

// Colorblind-friendly presets (no red-green distinctions)
const CHOROPLETH_COLOR_SCALES = {
  realtorch: {
    colors: ['#fef6f0', '#fde4d4', '#fcd2b8', '#d2691e', '#b85a1a', '#9e4b16', '#843c12', '#6a2d0e', '#50200a']
  },
  heat: {
    colors: ['#fff7bc', '#fee391', '#fec44f', '#fe9929', '#ec7014', '#cc4c02', '#993404', '#662506']
  },
  cool: {
    colors: ['#f7f7f7', '#d9d9d9', '#bdbdbd', '#969696', '#737373', '#525252', '#252525']
  },
  diverging: {
    colors: ['#8c510a', '#bf812d', '#dfc27d', '#f6e8c3', '#f5f5f5', '#c7eae5', '#80cdc1', '#35978f', '#01665e']
  },
  // Aliases for backward compatibility
  sequential_blue: {
    colors: ['#f7f7f7', '#d9d9d9', '#bdbdbd', '#969696', '#737373', '#525252', '#252525']  // -> cool
  },
  sequential_green: {
    colors: ['#fef6f0', '#fde4d4', '#fcd2b8', '#d2691e', '#b85a1a', '#9e4b16', '#843c12', '#6a2d0e', '#50200a']  // -> realtorch
  }
};

const CATEGORICAL_COLORS = [
  '#e41a1c',
  '#377eb8',
  '#4daf4a',
  '#984ea3',
  '#ff7f00',
  '#eab308',
  '#a65628',
  '#f781bf',
  '#999999',
  '#66c2a5',
  '#fc8d62',
  '#8da0cb'
];

const STATUS_COLORS = {
  active: '#4daf4a',
  pending: '#ff7f00',
  sold: '#377eb8',
  closed: '#377eb8',
  expired: '#999999',
  withdrawn: '#e41a1c',
  coming_soon: '#984ea3',
  new: '#66c2a5',
  price_reduced: '#fc8d62'
};

const MAP_ACCELERATION_HELP_TEXT =
  'To view maps, turn on graphics acceleration and relaunch your browser. Chrome: chrome://settings/system. Edge: edge://settings/system. Firefox: Settings > General > Performance.';

function safeParseJson(value) {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeLngLat(center) {
  if (!center) return null;
  if (Array.isArray(center) && center.length === 2) {
    const lng = Number(center[0]);
    const lat = Number(center[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
  }
  if (typeof center === 'string') {
    const parsed = safeParseJson(center);
    if (parsed) return normalizeLngLat(parsed);
  }
  if (typeof center === 'object' && center !== null) {
    if (Array.isArray(center) && center.length === 2) return normalizeLngLat(center);
    const lat = Number(center.lat ?? center.latitude);
    const lng = Number(center.lng ?? center.lon ?? center.longitude);
    if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
  }
  return null;
}

function normalizeBounds(bounds) {
  if (!Array.isArray(bounds) || bounds.length !== 2) return null;
  const a = bounds[0];
  const b = bounds[1];
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== 2 || b.length !== 2) return null;
  const minLng = Number(a[0]);
  const minLat = Number(a[1]);
  const maxLng = Number(b[0]);
  const maxLat = Number(b[1]);
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat]
  ];
}

function buildChoroplethMatchExpression({ geoidProperty, dataByGeoid, colors }) {
  const entries = Object.entries(dataByGeoid || {})
    .map(([geoid, raw]) => {
      const v = Number(raw);
      return [String(geoid), Number.isFinite(v) ? v : null];
    })
    .filter(([, v]) => v !== null);

  if (entries.length === 0) {
    return ['match', ['get', geoidProperty], '#cccccc'];
  }

  const vals = entries.map(([, v]) => v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;

  const matchPairs = [];
  for (const [geoid, value] of entries) {
    const normalized = (value - min) / range;
    const idx = Math.min(Math.floor(normalized * (colors.length - 1)), colors.length - 1);
    matchPairs.push(geoid, colors[idx]);
  }

  return ['match', ['get', geoidProperty], ...matchPairs, '#cccccc'];
}

function buildHexFillExpression(hexData, colorMode, gradientColor) {
  const entries = Object.entries(hexData || {});
  if (entries.length === 0) return '#cccccc';

  if (colorMode === 'categorical') {
    const sectorColors = {
      'Healthcare': '#e41a1c', 'Retail Trade': '#377eb8', 'Professional Services': '#4daf4a',
      'Construction': '#984ea3', 'Manufacturing': '#ff7f00', 'Education': '#eab308',
      'Accommodation & Food': '#a65628', 'Transportation': '#f781bf', 'Public Administration': '#999999',
      'Finance & Insurance': '#66c2a5', 'Admin Support': '#fc8d62', 'Wholesale Trade': '#8da0cb',
      'Other': '#cccccc', 'Agriculture': '#b2df8a', 'Mining': '#6a3d9a', 'Utilities': '#cab2d6',
      'Real Estate': '#fb9a99', 'Management': '#fdbf6f', 'Information': '#1f78b4',
      'Arts & Entertainment': '#b15928', 'Other Services': '#d9d9d9',
    };
    const matchPairs = [];
    for (const [hexId, info] of entries) {
      const sector = (info && typeof info === 'object') ? info.dominant_sector : '';
      const color = sectorColors[sector] || CATEGORICAL_COLORS[Object.keys(sectorColors).length % CATEGORICAL_COLORS.length];
      matchPairs.push(hexId, color);
    }
    return ['match', ['get', 'hex_id'], ...matchPairs, '#cccccc'];
  }

  if (colorMode === 'diverging') {
    // Uses `value` from hex_data — positive = teal (employment center / growth), negative = brown (bedroom / loss)
    // Use 70th percentile of absolute values as maxAbs so the top 30% saturate to max color
    // instead of outliers washing out the entire scale
    const vals = entries.map(([, info]) => (info && typeof info === 'object') ? (info.value || 0) : 0);
    const absVals = vals.map(v => Math.abs(v)).sort((a, b) => a - b);
    const p70Index = Math.min(Math.floor(absVals.length * 0.7), absVals.length - 1);
    const maxAbs = absVals[p70Index] || 1;
    const divergingColors = ['#8c510a', '#bf812d', '#dfc27d', '#f5f5f5', '#80cdc1', '#35978f', '#01665e'];
    const matchPairs = [];
    for (const [hexId, info] of entries) {
      const val = (info && typeof info === 'object') ? (info.value || 0) : 0;
      const normalized = Math.max(0, Math.min(1, (val + maxAbs) / (2 * maxAbs)));
      const idx = Math.min(Math.floor(normalized * (divergingColors.length - 1)), divergingColors.length - 1);
      matchPairs.push(hexId, divergingColors[idx]);
    }
    return ['match', ['get', 'hex_id'], ...matchPairs, '#cccccc'];
  }

  // Default gradient mode — single color scale based on `value`
  // Use 70th percentile as max so outlier hubs don't wash out the scale
  const vals = entries.map(([, info]) => (info && typeof info === 'object') ? (info.value || 0) : 0);
  const sorted = [...vals].sort((a, b) => a - b);
  const p70Index = Math.min(Math.floor(sorted.length * 0.7), sorted.length - 1);
  const min = sorted[0];
  const max = sorted[p70Index];
  const range = max - min || 1;

  // Pick color ramp based on optional gradientColor hint
  const COLOR_RAMPS = {
    '#7c3aed': ['#ede9fe', '#c4b5fd', '#8b5cf6', '#6d28d9', '#4c1d95'],  // violet
    '#3b82f6': ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a'],  // blue
    '#ec4899': ['#fce7f3', '#f9a8d4', '#ec4899', '#be185d', '#831843'],  // pink
  };
  const defaultRamp = ['#ede9fe', '#c4b5fd', '#8b5cf6', '#6d28d9', '#4c1d95'];  // violet
  const colors = COLOR_RAMPS[gradientColor] || defaultRamp;

  const matchPairs = [];
  for (const [hexId, info] of entries) {
    const val = (info && typeof info === 'object') ? (info.value || 0) : 0;
    const normalized = Math.max(0, Math.min(1, (val - min) / range));
    const idx = Math.min(Math.floor(normalized * (colors.length - 1)), colors.length - 1);
    matchPairs.push(hexId, colors[idx]);
  }
  return ['match', ['get', 'hex_id'], ...matchPairs, '#cccccc'];
}

function hexCssToRGBA(hex, alpha = 165) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b, alpha];
}

function buildH3HexLayer(hexData, hexColorMode, gradientColor, onHoverCb, beforeId) {
  const entries = Object.entries(hexData || {});
  if (entries.length === 0) return null;

  const sectorColors = {
    'Healthcare': '#e41a1c', 'Retail Trade': '#377eb8', 'Professional Services': '#4daf4a',
    'Construction': '#984ea3', 'Manufacturing': '#ff7f00', 'Education': '#eab308',
    'Accommodation & Food': '#a65628', 'Transportation': '#f781bf', 'Public Administration': '#999999',
    'Finance & Insurance': '#66c2a5', 'Admin Support': '#fc8d62', 'Wholesale Trade': '#8da0cb',
    'Other': '#cccccc', 'Agriculture': '#b2df8a', 'Mining': '#6a3d9a', 'Utilities': '#cab2d6',
    'Real Estate': '#fb9a99', 'Management': '#fdbf6f', 'Information': '#1f78b4',
    'Arts & Entertainment': '#b15928', 'Other Services': '#d9d9d9',
  };

  const colorMap = {};
  const defaultColor = [204, 204, 204, 165];

  if (hexColorMode === 'categorical') {
    for (const [hexId, info] of entries) {
      const sector = (info && typeof info === 'object') ? info.dominant_sector : '';
      const cssColor = sectorColors[sector] || '#cccccc';
      colorMap[hexId] = hexCssToRGBA(cssColor);
    }
  } else if (hexColorMode === 'diverging') {
    const vals = entries.map(([, info]) => (info && typeof info === 'object') ? (info.value || 0) : 0);
    const absVals = vals.map(v => Math.abs(v)).sort((a, b) => a - b);
    const p70Index = Math.min(Math.floor(absVals.length * 0.7), absVals.length - 1);
    const maxAbs = absVals[p70Index] || 1;
    const divergingColors = ['#8c510a', '#bf812d', '#dfc27d', '#f5f5f5', '#80cdc1', '#35978f', '#01665e'];
    for (const [hexId, info] of entries) {
      const val = (info && typeof info === 'object') ? (info.value || 0) : 0;
      const normalized = Math.max(0, Math.min(1, (val + maxAbs) / (2 * maxAbs)));
      const idx = Math.min(Math.floor(normalized * (divergingColors.length - 1)), divergingColors.length - 1);
      colorMap[hexId] = hexCssToRGBA(divergingColors[idx]);
    }
  } else {
    // Gradient mode — same 70th-percentile normalization
    const vals = entries.map(([, info]) => (info && typeof info === 'object') ? (info.value || 0) : 0);
    const sorted = [...vals].sort((a, b) => a - b);
    const p70Index = Math.min(Math.floor(sorted.length * 0.7), sorted.length - 1);
    const min = sorted[0];
    const max = sorted[p70Index];
    const range = max - min || 1;

    const COLOR_RAMPS = {
      '#7c3aed': ['#ede9fe', '#c4b5fd', '#8b5cf6', '#6d28d9', '#4c1d95'],
      '#3b82f6': ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a'],
      '#ec4899': ['#fce7f3', '#f9a8d4', '#ec4899', '#be185d', '#831843'],
    };
    const colors = COLOR_RAMPS[gradientColor] || ['#ede9fe', '#c4b5fd', '#8b5cf6', '#6d28d9', '#4c1d95'];

    for (const [hexId, info] of entries) {
      const val = (info && typeof info === 'object') ? (info.value || 0) : 0;
      const normalized = Math.max(0, Math.min(1, (val - min) / range));
      const idx = Math.min(Math.floor(normalized * (colors.length - 1)), colors.length - 1);
      colorMap[hexId] = hexCssToRGBA(colors[idx]);
    }
  }

  return new H3HexagonLayer({
    id: 'hex-fill',
    beforeId,
    data: entries.map(([id, info]) => ({ hex: id, ...(info && typeof info === 'object' ? info : { value: info }) })),
    getHexagon: d => d.hex,
    highPrecision: true,
    getFillColor: d => colorMap[d.hex] || defaultColor,
    getLineColor: [255, 255, 255, 128],
    getLineWidth: 1,
    lineWidthMinPixels: 0.4,
    extruded: false,
    opacity: 0.65,
    pickable: true,
    autoHighlight: true,
    highlightColor: [255, 255, 255, 80],
    onHover: onHoverCb,
  });
}

function buildDataPointExpressions({ points, colorMode, categoryColors }) {
  const colorValues = (points || []).map((p) => p?._color_value).filter((v) => v !== undefined && v !== null);
  const numericValues = colorValues.map((v) => Number(v)).filter((n) => Number.isFinite(n));

  if (colorMode === 'continuous' && numericValues.length > 0) {
    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const colors = CHOROPLETH_COLOR_SCALES.realtorch.colors;
    const expr = ['interpolate', ['linear'], ['to-number', ['get', '_color_value'], 0]];
    colors.forEach((c, i) => {
      const v = min + (max - min) * (i / (colors.length - 1));
      expr.push(v, c);
    });
    return { colorExpression: expr };
  }

  // Categorical mode: use custom category_colors if provided, else fall back to defaults
  const unique = Array.from(new Set(colorValues.map((v) => String(v)))).slice(0, 12);
  const match = ['match', ['get', '_color_value']];
  unique.forEach((val, i) => {
    // Check custom category_colors first, then STATUS_COLORS, then fallback
    const customColor = categoryColors?.[val];
    const key = val.toLowerCase().replaceAll(' ', '_');
    const c = customColor || STATUS_COLORS[key] || CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length];
    match.push(val, c);
  });
  match.push('#999999');
  return { colorExpression: match };
}

function getMapUnavailableError(error) {
  const message = error?.message || String(error || '');
  const isWebGLError = /webgl/i.test(message);
  return {
    title: isWebGLError ? 'Map unavailable' : 'Map failed to render',
    detail: isWebGLError
      ? `This browser could not initialize WebGL, so the map cannot be displayed here. ${MAP_ACCELERATION_HELP_TEXT}`
      : message || 'The map could not be displayed.'
  };
}

function MapLegend({ legendConfig }) {
  if (!legendConfig) return null;

  const { type, title, colors, minValue, maxValue, valueFormat, categories, maxItems } = legendConfig;

  const formatValue = (val) => {
    if (val === null || val === undefined || !Number.isFinite(val)) return 'N/A';
    if (valueFormat === 'percent') return `${val.toFixed(1)}%`;
    if (valueFormat === 'currency') return `$${val.toLocaleString()}`;
    if (Math.abs(val) >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  if (type === 'gradient' && colors && colors.length > 0) {
    const gradientColors = colors.join(', ');
    return (
      <div style={{
        position: 'absolute',
        bottom: 24,
        left: 12,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontSize: 12,
        zIndex: 10,
        minWidth: 140,
      }}>
        {title && <div style={{ fontWeight: 600, marginBottom: 6, color: '#1d1d1f' }}>{title}</div>}
        <div style={{
          height: 12,
          borderRadius: 3,
          background: `linear-gradient(to right, ${gradientColors})`,
          marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6e6e73', fontSize: 11 }}>
          <span>{formatValue(minValue)}</span>
          <span>{formatValue(maxValue)}</span>
        </div>
      </div>
    );
  }

  if (type === 'categorical' && categories && categories.length > 0) {
    return (
      <div style={{
        position: 'absolute',
        bottom: 24,
        left: 12,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontSize: 12,
        zIndex: 10,
        maxWidth: 180,
      }}>
        {title && <div style={{ fontWeight: 600, marginBottom: 8, color: '#1d1d1f' }}>{title}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {categories.slice(0, Number.isFinite(Number(maxItems)) && Number(maxItems) > 0 ? Number(maxItems) : 8).map((cat, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 14,
                height: cat.shape === 'line' ? 3 : 14,
                borderRadius: cat.shape === 'point' ? 7 : cat.shape === 'line' ? 1.5 : 3,
                background: cat.color,
                flexShrink: 0,
              }} />
              <span style={{ color: '#1d1d1f', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {cat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Styled maps (viz style v1): choropleth_map / data_point_map configs carrying meta.version >= 1.
// Everything visual (basemap, layers, classes, legend model, hover cards) comes from the shared
// atlasMapRuntime so chat and PDF exports match; this component owns the mapboxgl.Map lifecycle.
// ---------------------------------------------------------------------------
const STYLED_MAP_TYPES = ['choropleth_map', 'data_point_map', 'land_map'];
const STYLED_MAP_FIT_PADDING = 36;
const STYLED_MAP_LABEL_ATTEMPTS = 5;

export function isStyledMapConfig(cfg) {
  return !!cfg && STYLED_MAP_TYPES.includes(cfg.type) && AtlasMapRuntime.hasMeta(cfg);
}

function warnStyledMap(message, err) {
  // eslint-disable-next-line no-console
  console.warn(`[AtlasMapComponent] ${message}`, err);
}

function AtlasStyledMap({ mapConfig, showLegend = true }) {
  const containerRef = useRef(null);
  const theme = useAtlasTheme();
  const [mapError, setMapError] = useState(null);

  const legendModel = useMemo(() => {
    try {
      return AtlasMapRuntime.legendModel(mapConfig, theme);
    } catch (e) {
      warnStyledMap('legend failed:', e);
      return null;
    }
  }, [mapConfig, theme]);

  useEffect(() => {
    const cfg = mapConfig;
    setMapError(null);
    if (!containerRef.current) return undefined;

    mapboxgl.accessToken = DEFAULT_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken) {
      setMapError({ title: 'Map unavailable', detail: 'Mapbox token missing (set REACT_APP_MAPBOX_TOKEN).' });
      return undefined;
    }
    if (typeof mapboxgl.supported === 'function' && !mapboxgl.supported()) {
      setMapError({
        title: 'Map unavailable',
        detail: `This browser does not currently support WebGL maps. ${MAP_ACCELERATION_HELP_TEXT}`
      });
      return undefined;
    }

    let fitBox = null;
    try {
      fitBox = AtlasMapRuntime.bounds(cfg);
    } catch (e) {
      warnStyledMap('bounds failed:', e);
    }
    const center = normalizeLngLat(cfg.center) || [-98.5795, 39.8283];
    const zoom = cfg.zoom !== null && cfg.zoom !== undefined && Number.isFinite(Number(cfg.zoom)) ? Number(cfg.zoom) : 6;

    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: AtlasMapRuntime.basemapStyle(theme),
        center,
        zoom,
        // Start on the fitted view when we already know it, so the first frame does not jump.
        ...(fitBox ? { bounds: fitBox, fitBoundsOptions: { padding: STYLED_MAP_FIT_PADDING } } : {}),
        scrollZoom: false, // page scroll must not zoom the map; the +/- control does
        cooperativeGestures: COOPERATIVE_GESTURES,
      });
    } catch (e) {
      setMapError(getMapUnavailableError(e));
      return undefined;
    }

    let disposed = false;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    const onResize = () => {
      try {
        map.resize();
      } catch {
        // ignore
      }
    };
    window.addEventListener('resize', onResize);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    try {
      resizeObserver?.observe(containerRef.current);
    } catch {
      // ignore
    }

    map.on('error', (e) => warnStyledMap('Mapbox error:', e?.error || e));

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
      maxWidth: '280px',
      className: 'atlas-map-popup'
    });

    map.on('load', () => {
      if (disposed) return;
      try {
        AtlasMapRuntime.quietBasemap(map, theme);
        const firstSymbolId = map.getStyle().layers.find((l) => l.type === 'symbol')?.id;
        const info = AtlasMapRuntime.addLayers(map, cfg, { theme, beforeId: firstSymbolId });

        // Fit: rings / points / explicit bounds first, else the loaded area features.
        if (fitBox) {
          map.fitBounds(fitBox, { padding: STYLED_MAP_FIT_PADDING, duration: 0 });
        } else if (info.areaSource) {
          const onSourceData = (e) => {
            if (e.sourceId !== info.areaSource || !e.isSourceLoaded) return;
            const areaBox = AtlasMapRuntime.areaBounds(map, info);
            if (!areaBox) return;
            map.off('sourcedata', onSourceData);
            map.fitBounds(areaBox, { padding: STYLED_MAP_FIT_PADDING, duration: 0 });
          };
          map.on('sourcedata', onSourceData);
        }

        // Area name / value labels need the area tiles, so place them once the map settles.
        if (cfg.area_labels && info.areaSource) {
          let tries = 0;
          const onIdle = () => {
            tries += 1;
            let placed = false;
            try {
              placed = AtlasMapRuntime.addAreaLabels(map, cfg, info, { theme });
            } catch (e) {
              warnStyledMap('area labels failed:', e);
              placed = true;
            }
            if (placed || tries >= STYLED_MAP_LABEL_ATTEMPTS) map.off('idle', onIdle);
          };
          map.on('idle', onIdle);
        }

        // Hover cards (click too, for touch screens). Points win over the area beneath them.
        const pointLayers = (info.pointLayers || []).filter((id) => map.getLayer(id));
        const setCursor = (value) => {
          try {
            map.getCanvas().style.cursor = value;
          } catch {
            // ignore
          }
        };
        const pointAt = (evt) => {
          if (!pointLayers.length) return null;
          try {
            return map.queryRenderedFeatures(evt.point, { layers: pointLayers })[0] || null;
          } catch {
            return null;
          }
        };
        // addTo() on an open popup tears down and rebuilds its DOM, so only add it when closed.
        const openPopup = (lngLat, html) => {
          popup.setLngLat(lngLat).setHTML(html);
          if (!popup.isOpen()) popup.addTo(map);
        };
        const showPoint = (feature, evt) => {
          const coords = feature.geometry?.type === 'Point' ? feature.geometry.coordinates : null;
          openPopup(coords || evt.lngLat, AtlasMapRuntime.pointHoverHtml(cfg, feature));
        };
        const showArea = (feature, evt) => {
          openPopup(evt.lngLat, AtlasMapRuntime.areaHoverHtml(cfg, feature, info));
        };
        const hide = () => {
          setCursor('');
          popup.remove();
        };

        if (info.areaLayer) {
          const onArea = (evt) => {
            const feature = evt.features?.[0];
            if (!feature || pointAt(evt)) return;
            setCursor('pointer');
            showArea(feature, evt);
          };
          map.on('mousemove', info.areaLayer, onArea);
          map.on('click', info.areaLayer, onArea);
          map.on('mouseleave', info.areaLayer, hide);
        }
        pointLayers.forEach((layerId) => {
          const onPoint = (evt) => {
            const feature = evt.features?.[0];
            if (!feature) return;
            setCursor('pointer');
            showPoint(feature, evt);
          };
          map.on('mousemove', layerId, onPoint);
          map.on('click', layerId, onPoint);
          map.on('mouseleave', layerId, hide);
        });
        // A tap on empty map closes a card opened by a tap.
        const interactive = [info.areaLayer, ...pointLayers].filter(Boolean);
        map.on('click', (evt) => {
          if (!interactive.length) return;
          try {
            if (!map.queryRenderedFeatures(evt.point, { layers: interactive }).length) popup.remove();
          } catch {
            popup.remove();
          }
        });
      } catch (e) {
        setMapError(getMapUnavailableError(e));
      }
    });

    const t = setTimeout(onResize, 100);

    return () => {
      disposed = true;
      clearTimeout(t);
      try {
        resizeObserver?.disconnect();
      } catch {
        // ignore
      }
      window.removeEventListener('resize', onResize);
      try {
        popup.remove();
      } catch {
        // ignore
      }
      try {
        map.remove();
      } catch {
        // ignore
      }
    };
  }, [mapConfig, theme]);

  return (
    <div className="atlas-styled-map" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {mapError ? (
        <div className="atlas-map-unavailable" role="status">
          <div className="atlas-map-unavailable-title">{mapError.title}</div>
          <div className="atlas-map-unavailable-detail">{mapError.detail}</div>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="atlas-styled-map-canvas"
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 10,
          overflow: 'hidden',
          display: mapError ? 'none' : 'block'
        }}
      />
      {!mapError && showLegend ? <AtlasMapLegend model={legendModel} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styled land maps (land_map with meta.version >= 1): parcel / terrain / block / neighborhood, flat or 3D.
// Everything visual comes from atlasLandRuntime (the same file the PDF card inlines); this owns the map.
// ---------------------------------------------------------------------------
function AtlasLandMap({ mapConfig, showLegend = true }) {
  const containerRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const legendModel = useMemo(() => {
    try {
      return AtlasLandRuntime.legendModel(mapConfig);
    } catch (e) {
      warnStyledMap('land legend failed:', e);
      return null;
    }
  }, [mapConfig]);

  useEffect(() => {
    const cfg = mapConfig;
    setMapError(null);
    if (!containerRef.current) return undefined;
    mapboxgl.accessToken = DEFAULT_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken) {
      setMapError({ title: 'Map unavailable', detail: 'Mapbox token missing (set REACT_APP_MAPBOX_TOKEN).' });
      return undefined;
    }
    if (typeof mapboxgl.supported === 'function' && !mapboxgl.supported()) {
      setMapError({ title: 'Map unavailable', detail: `This browser does not currently support WebGL maps. ${MAP_ACCELERATION_HELP_TEXT}` });
      return undefined;
    }
    const cam = AtlasLandRuntime.camera(cfg);
    const threeD = cam.pitch > 0 || cam.terrain3d;
    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: AtlasLandRuntime.basemapStyle(cfg),
        center: normalizeLngLat(cfg.center) || [-98.5795, 39.8283],
        zoom: Number.isFinite(Number(cfg.zoom)) ? Number(cfg.zoom) : 16,
        pitch: cam.pitch,
        bearing: cam.bearing,
        maxPitch: 85,
        antialias: true,
        scrollZoom: false, // page scroll must not zoom the map; the +/- control does
        cooperativeGestures: COOPERATIVE_GESTURES,
        dragRotate: threeD, // 3D views can be orbited (right-drag / two fingers); flat views stay north-up
        touchPitch: threeD,
      });
    } catch (e) {
      setMapError(getMapUnavailableError(e));
      return undefined;
    }
    let disposed = false;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: threeD, visualizePitch: threeD }), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial', maxWidth: 90 }), 'bottom-left');
    const onResize = () => {
      try {
        map.resize();
      } catch {
        // ignore
      }
    };
    window.addEventListener('resize', onResize);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    try {
      resizeObserver?.observe(containerRef.current);
    } catch {
      // ignore
    }
    map.on('error', (e) => warnStyledMap('Mapbox error:', e?.error || e));
    const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12, maxWidth: '280px', className: 'atlas-map-popup' });

    map.on('load', () => {
      if (disposed) return;
      try {
        const out = AtlasLandRuntime.draw(map, cfg, { padding: 36 });
        const hoverable = (out.hoverable || []).filter((id) => map.getLayer(id));
        const show = (evt) => {
          const feature = evt.features?.[0];
          if (!feature) return;
          const html = AtlasLandRuntime.hoverHtml(feature.properties);
          if (!html) return;
          try {
            map.getCanvas().style.cursor = 'pointer';
          } catch {
            // ignore
          }
          popup.setLngLat(evt.lngLat).setHTML(html);
          if (!popup.isOpen()) popup.addTo(map);
        };
        const hide = () => {
          try {
            map.getCanvas().style.cursor = '';
          } catch {
            // ignore
          }
          popup.remove();
        };
        // Markers win over the cells / polygons beneath them: bind the top-most layers first.
        hoverable.slice().reverse().forEach((id) => {
          map.on('mousemove', id, show);
          map.on('click', id, show);
          map.on('mouseleave', id, hide);
        });
      } catch (e) {
        setMapError(getMapUnavailableError(e));
      }
    });
    const t = setTimeout(onResize, 100);
    return () => {
      disposed = true;
      clearTimeout(t);
      try {
        resizeObserver?.disconnect();
      } catch {
        // ignore
      }
      window.removeEventListener('resize', onResize);
      try {
        popup.remove();
      } catch {
        // ignore
      }
      try {
        map.remove();
      } catch {
        // ignore
      }
    };
  }, [mapConfig]);

  return (
    <div className="atlas-styled-map" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {mapError ? (
        <div className="atlas-map-unavailable" role="status">
          <div className="atlas-map-unavailable-title">{mapError.title}</div>
          <div className="atlas-map-unavailable-detail">{mapError.detail}</div>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="atlas-styled-map-canvas"
        style={{ width: '100%', height: '100%', borderRadius: 10, overflow: 'hidden', display: mapError ? 'none' : 'block' }}
      />
      {!mapError && showLegend ? <AtlasMapLegend model={legendModel} /> : null}
    </div>
  );
}

// showLegend=false: the caller (AtlasMapView's card) draws the legend as a strip outside the map.
export default function AtlasMapComponent({ mapConfig, showLegend = true }) {
  if (isStyledMapConfig(mapConfig) && mapConfig.type === 'land_map') return <AtlasLandMap mapConfig={mapConfig} showLegend={showLegend} />;
  if (isStyledMapConfig(mapConfig)) return <AtlasStyledMap mapConfig={mapConfig} showLegend={showLegend} />;
  return <LegacyAtlasMapComponent mapConfig={mapConfig} />;
}

// ---------------------------------------------------------------------------
// Legacy renderer: every config without meta (and every other map type) renders exactly as before.
// ---------------------------------------------------------------------------
function LegacyAtlasMapComponent({ mapConfig }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [legendConfig, setLegendConfig] = useState(null);
  const [mapNote, setMapNote] = useState(null);

  const cfg = mapConfig || {};
  const configType = cfg.type || '';

  // land_map names its basemap (satellite | streets | light); every other type may pass a style URL.
  const mapboxStyle =
    configType === 'land_map' ? basemapStyleUrl(cfg.basemap || cfg.style) : cfg.style || 'mapbox://styles/mapbox/light-v11';
  const center = useMemo(() => normalizeLngLat(cfg.center) || [-98.5795, 39.8283], [cfg.center]);
  const zoom = Number.isFinite(Number(cfg.zoom)) ? Number(cfg.zoom) : 6;
  const bounds = useMemo(() => normalizeBounds(cfg.bounds), [cfg.bounds]);

  useEffect(() => {
    setMapError(null);
    setLegendConfig(null);
    setMapNote(null);
    if (!containerRef.current) return undefined;

    // Always set the token to ensure we use the correct one (fixes caching issues)
    mapboxgl.accessToken = DEFAULT_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken) {
      setMapError({
        title: 'Map unavailable',
        detail: 'Mapbox token missing (set REACT_APP_MAPBOX_TOKEN).'
      });
      return undefined;
    }

    // Cleanup prior map instance (React strict mode / token re-renders).
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
    }

    if (typeof mapboxgl.supported === 'function' && !mapboxgl.supported()) {
      setMapError({
        title: 'Map unavailable',
        detail: `This browser does not currently support WebGL maps. ${MAP_ACCELERATION_HELP_TEXT}`
      });
      return undefined;
    }

    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: mapboxStyle,
        center,
        zoom,
        scrollZoom: false, // Disable scroll-to-zoom to prevent accidental zooming when scrolling the page
        cooperativeGestures: COOPERATIVE_GESTURES,
      });
    } catch (e) {
      setMapError(getMapUnavailableError(e));
      return undefined;
    }
    mapRef.current = map;

    // Add navigation controls (zoom buttons, compass) - users can use these to zoom
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Keep the map in sync with container size changes (critical for correct marker/layer alignment).
    const onResize = () => {
      try {
        map.resize();
      } catch {
        // ignore
      }
    };
    window.addEventListener('resize', onResize);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            onResize();
          })
        : null;
    try {
      resizeObserver?.observe(containerRef.current);
    } catch {
      // ignore
    }

    map.on('error', (e) => {
      // eslint-disable-next-line no-console
      console.warn('[AtlasMapComponent] Mapbox error:', e?.error || e);
    });

    map.on('load', () => {
      try {
        // Find the first symbol (label) layer so we can insert data layers beneath it
        const firstSymbolId = map.getStyle().layers.find(l => l.type === 'symbol')?.id;

        if (bounds && configType !== 'land_map') {
          map.fitBounds(bounds, { padding: 24, duration: 0 });
        }

        // -----------------------------------------------------------------
        // Choropleth (monolith-style tileset rendering)
        // -----------------------------------------------------------------
        if (configType === 'choropleth' || configType === 'choropleth_with_points' || configType === 'choropleth_map') {
          const geoLevel = cfg.geography_level || 'county';
          const tileset = MAPBOX_TILESETS[geoLevel];
          if (!tileset) throw new Error(`Unsupported geography_level: ${geoLevel}`);

          const sourceId = `atlas-choropleth-${geoLevel}`;
          const fillLayerId = `atlas-choropleth-fill-${geoLevel}`;
          const lineLayerId = `atlas-choropleth-line-${geoLevel}`;

          // New color mode system
          const colorMode = cfg.color_mode || 'gradient';

          // Get colors: prefer explicit colors array, then look up preset
          let colors;
          if (Array.isArray(cfg.colors) && cfg.colors.length > 0) {
            colors = cfg.colors;
          } else {
            const scaleKey = cfg.color_scale || 'realtorch';
            const scale = CHOROPLETH_COLOR_SCALES[scaleKey] || CHOROPLETH_COLOR_SCALES.realtorch;
            colors = [...scale.colors];
          }
          if (cfg.reverse_colors) {
            colors = [...colors].reverse();
          }

          const dataByGeoid =
            cfg.data && typeof cfg.data === 'object'
              ? Object.fromEntries(
                  Object.entries(cfg.data).map(([k, v]) => [k, typeof v === 'object' && v !== null ? v.value : v])
                )
              : {};

          // Build fill color expression based on color_mode
          let fillColorExpr;

          if (colorMode === 'uniform') {
            // Uniform: all features get the same color
            const uniformColor = cfg.fill_color || '#d2691e';
            const geoids = Object.keys(dataByGeoid);
            if (geoids.length > 0) {
              fillColorExpr = ['match', ['get', tileset.geoid_property], ...geoids.flatMap(g => [g, uniformColor]), '#cccccc'];
            } else {
              fillColorExpr = uniformColor;
            }
          } else if (colorMode === 'categorical') {
            // Categorical: map categories to colors
            const categoryColors = cfg.category_colors || {};
            const uniqueValues = [...new Set(Object.values(dataByGeoid).map(v => String(v)))];
            const matchPairs = [];

            for (const [geoid, value] of Object.entries(dataByGeoid)) {
              const strVal = String(value);
              // Use explicit category color, or assign from palette
              const color = categoryColors[strVal] ||
                CATEGORICAL_COLORS[uniqueValues.indexOf(strVal) % CATEGORICAL_COLORS.length];
              matchPairs.push(geoid, color);
            }

            if (matchPairs.length > 0) {
              fillColorExpr = ['match', ['get', tileset.geoid_property], ...matchPairs, '#cccccc'];
            } else {
              fillColorExpr = '#cccccc';
            }
          } else {
            // Gradient: numeric values mapped to color scale (default)
            fillColorExpr = cfg.match_expression || buildChoroplethMatchExpression({
              geoidProperty: tileset.geoid_property,
              dataByGeoid,
              colors
            });
          }

          map.addSource(sourceId, {
            type: 'vector',
            url: `mapbox://${tileset.id}`
          });

          // Only render geographies that exist in the data (hide all others)
          const dataGeoids = Object.keys(dataByGeoid);
          const geoFilter = dataGeoids.length > 0
            ? ['in', ['get', tileset.geoid_property], ['literal', dataGeoids]]
            : null;

          map.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            'source-layer': tileset.source_layer,
            ...(geoFilter ? { filter: geoFilter } : {}),
            paint: {
              'fill-color': fillColorExpr,
              'fill-opacity': Number.isFinite(Number(cfg.fill_opacity)) ? Number(cfg.fill_opacity) : 0.7
            }
          }, firstSymbolId);

          map.addLayer({
            id: lineLayerId,
            type: 'line',
            source: sourceId,
            'source-layer': tileset.source_layer,
            ...(geoFilter ? { filter: geoFilter } : {}),
            paint: {
              'line-color': cfg.outline_color || '#ffffff',
              'line-width': 0.6
            }
          }, firstSymbolId);

          // Auto-fit to data features when no explicit bounds provided
          if (!bounds && dataGeoids.length > 0) {
            const fitToFeatures = () => {
              const features = map.querySourceFeatures(sourceId, { sourceLayer: tileset.source_layer, filter: geoFilter });
              if (features.length === 0) return false;
              const fitBounds = new mapboxgl.LngLatBounds();
              for (const f of features) {
                const coords = f.geometry?.coordinates;
                if (!coords) continue;
                const flatten = (arr) => {
                  if (typeof arr[0] === 'number') { fitBounds.extend(arr); return; }
                  for (const item of arr) flatten(item);
                };
                flatten(coords);
              }
              if (!fitBounds.isEmpty()) {
                map.fitBounds(fitBounds, { padding: 40, duration: 0 });
              }
              return true;
            };
            // Try once tiles are loaded; sourcedata fires per-tile so listen until we get features
            const onSourceData = (e) => {
              if (e.sourceId === sourceId && e.isSourceLoaded) {
                if (fitToFeatures()) map.off('sourcedata', onSourceData);
              }
            };
            map.on('sourcedata', onSourceData);
          }

          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
          map.on('mousemove', fillLayerId, (evt) => {
            map.getCanvas().style.cursor = 'pointer';
            const feature = evt.features?.[0];
            if (!feature) return;
            const geoid = feature.properties?.[tileset.geoid_property];
            const name = tileset.name_property ? feature.properties?.[tileset.name_property] : geoid;
            const rawVal = dataByGeoid[String(geoid)];

            const label = cfg.value_label || cfg.legend_title || 'Value';
            const v = Number(rawVal);
            const formatted = colorMode === 'categorical' ? String(rawVal || 'N/A') :
              (Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : 'N/A');

            popup.setLngLat(evt.lngLat).setHTML(`<div><strong>${name}</strong><br/>${label}: ${formatted}</div>`).addTo(map);
          });
          map.on('mouseleave', fillLayerId, () => {
            map.getCanvas().style.cursor = '';
            popup.remove();
          });

          // Build legend config based on color_mode
          if (colorMode === 'uniform') {
            // No legend needed for uniform coloring (or simple "Selected Areas" label)
            setLegendConfig({
              type: 'categorical',
              title: cfg.legend_title || 'Selected Areas',
              categories: [{ label: cfg.title || 'Selected', color: cfg.fill_color || '#d2691e' }]
            });
          } else if (colorMode === 'categorical') {
            const categoryColors = cfg.category_colors || {};
            const uniqueValues = [...new Set(Object.values(dataByGeoid).map(v => String(v)))];
            const categories = uniqueValues.slice(0, 8).map((val, i) => ({
              label: val,
              color: categoryColors[val] || CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length]
            }));
            if (categories.length > 0) {
              setLegendConfig({
                type: 'categorical',
                title: cfg.legend_title || cfg.value_label || 'Category',
                categories
              });
            }
          } else {
            // Gradient legend
            const vals = Object.values(dataByGeoid).map(v => Number(v)).filter(Number.isFinite);
            if (vals.length > 0) {
              const minVal = Math.min(...vals);
              const maxVal = Math.max(...vals);
              setLegendConfig({
                type: 'gradient',
                title: cfg.legend_title || cfg.value_label || cfg.title || 'Value',
                colors: colors,
                minValue: minVal,
                maxValue: maxVal,
                valueFormat: cfg.value_format || 'number',
              });
            }
          }
        }

        // -----------------------------------------------------------------
        // Data-driven point layer (GeoJSON circles)
        // -----------------------------------------------------------------
        else if (configType === 'data_points' || configType === 'data_point_map') {
          const sourceId = `atlas-datapoints`;
          const layerId = `atlas-datapoints-circles`;

          const pts = Array.isArray(cfg.points) ? cfg.points : [];
          const colorMode = cfg.style_hints?.color_mode || cfg.color_mode || 'categorical';
          const opacity = Number.isFinite(Number(cfg.style_hints?.opacity)) ? Number(cfg.style_hints.opacity) : 0.85;

          const features = pts
            .map((p) => {
              const lat = Number(p.lat);
              const lng = Number(p.lng);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
              const popup = p.popup && typeof p.popup === 'object' ? p.popup : {};
              const props = { ...popup };
              if (p.color_value !== undefined) props._color_value = String(p.color_value);
              if (p.size_value !== undefined) props._size_value = String(p.size_value);
              if (!props._label && popup.label) props._label = String(popup.label);
              return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lng, lat] },
                properties: props
              };
            })
            .filter(Boolean);

          const categoryColors = cfg.style_hints?.category_colors || null;
          const { colorExpression } = buildDataPointExpressions({
            points: features.map((f) => f.properties),
            colorMode,
            categoryColors
          });

          map.addSource(sourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features }
          });

          map.addLayer({
            id: layerId,
            type: 'circle',
            source: sourceId,
            paint: {
              'circle-color': colorExpression || '#d2691e',
              'circle-radius': 8,
              'circle-opacity': opacity,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff'
            }
          }, firstSymbolId);

          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
          map.on('mouseenter', layerId, () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', layerId, () => {
            map.getCanvas().style.cursor = '';
            popup.remove();
          });
          map.on('mousemove', layerId, (evt) => {
            const feature = evt.features?.[0];
            if (!feature) return;
            const props = feature.properties || {};
            const entries = Object.entries(props).filter(([k]) => !k.startsWith('_'));
            const title = props._label ? `<div><strong>${props._label}</strong></div>` : '';
            const rows = entries
              .slice(0, 8)
              .map(([k, v]) => `<div><strong>${k}:</strong> ${String(v)}</div>`)
              .join('');
            popup.setLngLat(evt.lngLat).setHTML(`${title}${rows}`).addTo(map);
          });

          // Build legend config for point maps
          const colorCol = cfg.style_hints?.color_column || cfg.color_column;
          if (colorCol && colorMode === 'categorical') {
            const colorValues = pts.map(p => p.color_value).filter(v => v !== undefined && v !== null);
            const unique = Array.from(new Set(colorValues.map(v => String(v)))).slice(0, 12);
            const categories = unique.map((val, i) => {
              // Use custom category_colors first, then STATUS_COLORS, then fallback
              const customColor = categoryColors?.[val];
              const key = val.toLowerCase().replaceAll(' ', '_');
              const color = customColor || STATUS_COLORS[key] || CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length];
              return { label: val, color };
            });
            if (categories.length > 0) {
              setLegendConfig({
                type: 'categorical',
                title: cfg.legend_title || colorCol || 'Category',
                categories,
              });
            }
          } else if (colorCol && colorMode === 'continuous') {
            const numericValues = pts.map(p => Number(p.color_value)).filter(Number.isFinite);
            if (numericValues.length > 0) {
              setLegendConfig({
                type: 'gradient',
                title: cfg.legend_title || colorCol || 'Value',
                colors: CHOROPLETH_COLOR_SCALES.realtorch.colors,
                minValue: Math.min(...numericValues),
                maxValue: Math.max(...numericValues),
                valueFormat: cfg.value_format || 'number',
              });
            }
          }
        }

        // -----------------------------------------------------------------
        // Land map (server-styled parcel / zoning / footprints / places layers)
        // -----------------------------------------------------------------
        else if (configType === 'land_map') {
          const landBounds = landMapBounds(cfg);
          if (landBounds) {
            map.fitBounds(landBounds, { padding: LAND_MAP_FIT_PADDING, maxZoom: LAND_MAP_FIT_MAX_ZOOM, duration: 0 });
          }
          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12, maxWidth: '280px' });
          addLandMapLayers(map, cfg, { beforeId: firstSymbolId, popupRef: { current: popup } });
          const legend = legendItemsFromConfig(cfg);
          if (legend) setLegendConfig(legend);
          if (cfg.attribution_note) setMapNote(String(cfg.attribution_note));
        }

        // -----------------------------------------------------------------
        // Simple point map (markers)
        // -----------------------------------------------------------------
        else if (Array.isArray(cfg.points)) {
          // Render as a GeoJSON circle layer (avoids DOM marker drift if the container is resized).
          const sourceId = `atlas-simplepoints`;
          const layerId = `atlas-simplepoints-circles`;

          const pts = Array.isArray(cfg.points) ? cfg.points : [];
          const features = pts
            .map((p) => {
              const lat = Number(p.lat);
              const lng = Number(p.lng);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
              return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lng, lat] },
                properties: {
                  label: p.label || '',
                  description: p.description || '',
                  color: p.color || '#d2691e'
                }
              };
            })
            .filter(Boolean);

          map.addSource(sourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features }
          });

          map.addLayer({
            id: layerId,
            type: 'circle',
            source: sourceId,
            paint: {
              'circle-color': ['coalesce', ['get', 'color'], '#d2691e'],
              'circle-radius': 7,
              'circle-opacity': 0.95,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff'
            }
          }, firstSymbolId);

          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
          map.on('mouseenter', layerId, () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', layerId, () => {
            map.getCanvas().style.cursor = '';
            popup.remove();
          });
          map.on('mousemove', layerId, (evt) => {
            const feature = evt.features?.[0];
            if (!feature) return;
            const props = feature.properties || {};
            const label = props.label ? `<div><strong>${props.label}</strong></div>` : '';
            const desc = props.description ? `<div>${props.description}</div>` : '';
            popup.setLngLat(evt.lngLat).setHTML(`${label}${desc}`).addTo(map);
          });
        }
        // -----------------------------------------------------------------
        // Hex flow map (LODES commute analysis)
        // -----------------------------------------------------------------
        else if (configType === 'hex_flow_map') {
          const hexData = cfg.hex_data || {};
          const hexColorMode = cfg.hex_color_mode || 'gradient';

          // Hex popup — mode-aware content (driven by deck.gl onHover)
          const hexPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });

          const onHexHover = ({object, coordinate}) => {
            if (!object || !coordinate) {
              hexPopup.remove();
              map.getCanvas().style.cursor = '';
              return;
            }
            map.getCanvas().style.cursor = 'pointer';
            const info = object;
            const val = info.value || 0;
            let html = '<div>';

            if (hexColorMode === 'diverging') {
              const localJobs = info.local_jobs;
              const residentWorkers = info.resident_workers;
              if (localJobs !== undefined && residentWorkers !== undefined) {
                html += `<strong>Jobs:</strong> ${localJobs.toLocaleString()}<br/>`;
                html += `<strong>Residents:</strong> ${residentWorkers.toLocaleString()}<br/>`;
                html += `<strong>Net:</strong> ${val > 0 ? '+' : ''}${val.toLocaleString()}`;
              } else {
                html += `<strong>Change:</strong> ${val > 0 ? '+' : ''}${val.toLocaleString()}`;
                if (info.pct_change !== undefined) {
                  html += ` (${info.pct_change > 0 ? '+' : ''}${info.pct_change.toFixed(1)}%)`;
                }
              }
            } else if (hexColorMode === 'categorical') {
              html += `<strong>Total Jobs:</strong> ${val.toLocaleString()}<br/>`;
              if (info.sectors && Array.isArray(info.sectors) && info.sectors.length > 0) {
                html += '<div style="margin-top:4px;max-height:180px;overflow-y:auto">';
                for (const s of info.sectors) {
                  const pct = val > 0 ? ((s.jobs / val) * 100).toFixed(0) : 0;
                  html += `<div style="display:flex;justify-content:space-between;gap:8px;font-size:12px;line-height:1.5"><span>${s.name}</span><span style="white-space:nowrap"><strong>${s.jobs.toLocaleString()}</strong> (${pct}%)</span></div>`;
                }
                html += '</div>';
              } else if (info.dominant_sector) {
                html += `<strong>Sector:</strong> ${info.dominant_sector}`;
              }
            } else {
              html += `<strong>Workers:</strong> ${val.toLocaleString()}`;
            }

            if (info.dominant_sector && hexColorMode !== 'categorical') {
              html += `<br/><em>${info.dominant_sector}</em>`;
            }
            html += '</div>';
            hexPopup.setLngLat(coordinate).setHTML(html).addTo(map);
          };

          // deck.gl H3HexagonLayer — renders hex polygons client-side from H3 hex IDs
          const hexLayer = buildH3HexLayer(hexData, hexColorMode, cfg.gradient_color, onHexHover, firstSymbolId);
          if (hexLayer) {
            const deckOverlay = new MapboxOverlay({ interleaved: true, layers: [hexLayer] });
            map.addControl(deckOverlay);
          }

          // Road arteries layer (Mapbox GeoJSON — renders on top of deck.gl hexes via interleaved mode)
          if (cfg.arteries_geojson) {
            const artVols = (cfg.arteries_geojson.features || []).map(f => f.properties?.volume || 0);
            const lowVol = Math.min(...artVols) || 0;
            const highVol = Math.max(...artVols) || 1;
            map.addSource('arteries', { type: 'geojson', data: cfg.arteries_geojson });
            map.addLayer({
              id: 'arteries-line', type: 'line',
              source: 'arteries',
              paint: {
                'line-color': ['interpolate', ['linear'], ['get', 'volume'], lowVol, '#14b8a6', highVol, '#0f766e'],
                'line-width': ['interpolate', ['linear'], ['get', 'volume'], lowVol, 2, highVol, 8],
                'line-opacity': 0.75
              },
              layout: { 'line-cap': 'round', 'line-join': 'round' }
            }, firstSymbolId);
          }

          // Artery popup (still Mapbox mousemove — arteries are a Mapbox layer)
          if (cfg.arteries_geojson) {
            const artPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
            map.on('mousemove', 'arteries-line', (evt) => {
              const feature = evt.features?.[0];
              if (!feature) return;
              const vol = feature.properties?.volume || 0;
              const name = feature.properties?.road_name || 'Road';
              artPopup.setLngLat(evt.lngLat).setHTML(`<div><strong>${name}</strong><br/>Commuters: ${Math.round(vol).toLocaleString()}</div>`).addTo(map);
            });
            map.on('mouseleave', 'arteries-line', () => { artPopup.remove(); });
          }

          // Legend
          const legendItems = cfg.legend?.items || [];
          if (legendItems.length > 0) {
            setLegendConfig({
              type: 'categorical',
              title: cfg.title || 'Commute Patterns',
              categories: legendItems.map(item => ({ label: item.label, color: item.color || '#3b82f6' }))
            });
          }
        }

      } catch (e) {
        setMapError(getMapUnavailableError(e));
      }
    });

    // Make sure the map measures the container after layout.
    const t = setTimeout(() => {
      try {
        map.resize();
      } catch {
        // ignore
      }
    }, 100);

    return () => {
      clearTimeout(t);
      try {
        resizeObserver?.disconnect();
      } catch {
        // ignore
      }
      window.removeEventListener('resize', onResize);
      try {
        map.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapConfig]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {mapError ? (
        <div className="atlas-map-unavailable" role="status">
          <div className="atlas-map-unavailable-title">{mapError.title}</div>
          <div className="atlas-map-unavailable-detail">{mapError.detail}</div>
        </div>
      ) : null}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 10,
          overflow: 'hidden',
          display: mapError ? 'none' : 'block'
        }}
      />
      {!mapError ? <MapLegend legendConfig={legendConfig} /> : null}
      {!mapError && mapNote ? (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            maxWidth: '70%',
            background: 'rgba(255, 255, 255, 0.92)',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 11,
            lineHeight: 1.3,
            color: '#3a3a3c',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          {mapNote}
        </div>
      ) : null}
    </div>
  );
}
